import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Lightning, GoogleLogo, Eye, EyeSlash } from '@phosphor-icons/react';

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage({ defaultMode = 'login' }) {
  const [mode, setMode] = useState(defaultMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const emailRef = useRef(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, [mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      navigate('/contact');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/contact';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Lightning size={28} weight="fill" className="text-[#002FA7]" />
            <h1 className="text-3xl font-black tracking-tight text-[#111827]" data-testid="app-title">SalesCrunch<span className="text-[#F39C12]">CRM</span></h1>
          </div>
          <p className="text-sm text-[#4B5563]">Fast. Simple. No bloat.</p>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-sm p-6">
          {/* Tab Toggle */}
          <div className="flex mb-6 border border-[#E5E7EB] rounded-sm overflow-hidden">
            <button
              type="button"
              onClick={() => { setMode('login'); setError(''); }}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                mode === 'login' ? 'bg-[#002FA7] text-white' : 'text-[#4B5563] hover:bg-gray-50'
              }`}
              data-testid="login-tab"
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => { setMode('register'); setError(''); }}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                mode === 'register' ? 'bg-[#002FA7] text-white' : 'text-[#4B5563] hover:bg-gray-50'
              }`}
              data-testid="register-tab"
            >
              Register
            </button>
          </div>

          {error && (
            <div className="mb-4 p-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <Label htmlFor="name" className="text-xs font-medium text-[#4B5563] uppercase tracking-wide">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required={mode === 'register'}
                  className="mt-1 rounded-sm focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
                  data-testid="register-name-input"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs font-medium text-[#4B5563] uppercase tracking-wide">Email</Label>
              <Input
                ref={emailRef}
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="mt-1 rounded-sm focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
                data-testid="email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-medium text-[#4B5563] uppercase tracking-wide">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  className="rounded-sm focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7] pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#111827] p-1"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#002FA7] hover:bg-[#002080] text-white rounded-sm font-medium"
              data-testid="auth-submit-btn"
            >
              {submitting ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </Button>
          </form>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E5E7EB]" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-[#4B5563]">or</span></div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            className="w-full rounded-sm border-[#E5E7EB] text-[#111827] hover:bg-gray-50 gap-2"
            data-testid="google-login-btn"
          >
            <GoogleLogo size={18} weight="bold" />
            Continue with Google
          </Button>

          <div className="mt-5 pt-4 border-t border-[#F3F4F6] text-center">
            <button
              type="button"
              onClick={async () => {
                try {
                  localStorage.clear();
                  sessionStorage.clear();
                  if ('caches' in window) {
                    const names = await caches.keys();
                    await Promise.all(names.map(n => caches.delete(n)));
                  }
                  if ('serviceWorker' in navigator) {
                    const regs = await navigator.serviceWorker.getRegistrations();
                    await Promise.all(regs.map(r => r.unregister()));
                  }
                } catch {}
                window.location.href = '/login?_=' + Date.now();
              }}
              className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 text-[14px] font-semibold text-white bg-[#F39C12] hover:bg-[#E67E22] rounded-sm shadow-sm active:scale-[0.99] transition-all"
              data-testid="clear-cache-btn"
            >
              <span aria-hidden>🔄</span>
              Trouble signing in? Force-refresh
            </button>
            <p className="text-[11px] text-[#6B7280] mt-2 leading-snug">
              Wipes your browser cache and reloads with the latest version. Safe to use anytime.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
